import { com } from '@eidu/web'
import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import EqualityHashMap from '../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import getEntitiesOfType from './api/entity/data/getEntitiesOfType'
import { getRelated } from '../domain/entity/getEntitiesFromResponses'
import { entityKeys } from './queryKeys'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import Entity = com.eidu.sharedlib.entity.Entity
import KeysetPaginatedResponseWithRelated = com.eidu.api.pagination.KeysetPaginatedResponseWithRelated
import PaginatedResponseWithRelated = com.eidu.api.pagination.PaginatedResponseWithRelated

export type UsePaginatedEntitiesResult = {
  entities: EntityWithLabelAndRelated[]
  loadMore: () => void
  reload: () => void
  hasMore: boolean
  loading: boolean
}

type UsePaginatedEntitiesParams = {
  typeId: EntityTypeId
  searchQuery?: string
  filters?: Map<FieldId, string>
  allTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  pageSize?: number
}

const DEFAULT_PAGE_SIZE = 50

const getEntitiesFromResponse = (
  response: PaginatedResponseWithRelated<Entity>,
  types: ReadonlyMap<EntityTypeId, EntityType | null>
): [EntityId, EntityWithLabelAndRelated][] => {
  const relatedEntitiesById =
    response.relatedDocuments
      ?.asJsReadonlyArrayView()
      ?.let((related) => new EqualityHashMap(Array.from(related).map((it) => [it.id, it]))) ??
    new Map<EntityId, Entity>()

  return response.documents.asJsReadonlyArrayView().map((entity) => getRelated(entity, types, relatedEntitiesById))
}

const usePaginatedEntitiesOfType = ({
  typeId,
  searchQuery,
  filters,
  allTypes,
  pageSize = DEFAULT_PAGE_SIZE,
}: UsePaginatedEntitiesParams): UsePaginatedEntitiesResult => {
  const filtersArray = filters ? Array.from(filters.entries()) : []

  const query = useInfiniteQuery({
    queryKey: [...entityKeys.ofType(typeId), 'paginated', searchQuery ?? '', filtersArray],
    queryFn: async ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) => {
      const response = await getEntitiesOfType({
        id: typeId,
        pageSize,
        cursor: pageParam || '',
        query: searchQuery ?? '',
        filters: filtersArray.map(([fieldId, value]) => ({
          fieldId: fieldId.toString(),
          value,
        })),
        withRelatedEntities: true,
        signal,
      })

      if (response instanceof KeysetPaginatedResponseWithRelated) {
        const entities = getEntitiesFromResponse(response, allTypes).map(([_, entity]) => entity)
        return {
          entities,
          nextCursor: response.next || undefined,
        }
      } else {
        throw new Error('Expected cursor-based pagination response')
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined,
  })

  const entities = useMemo(
    () =>
      query.data?.pages.flatMap(
        (page: { entities: EntityWithLabelAndRelated[]; nextCursor: string | undefined }) => page.entities
      ) ?? [],
    [query.data?.pages, query.dataUpdatedAt]
  )

  const loading = query.isLoading || query.isFetchingNextPage || query.isRefetching
  const hasMore = query.hasNextPage ?? false

  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }, [query])

  const reload = useCallback(() => {
    query.refetch()
  }, [query])

  return { entities, loadMore, reload, hasMore, loading }
}

export default usePaginatedEntitiesOfType
